import { nextTick, type Ref } from 'vue';
import { useRepo } from 'pinia-orm';
import { useEventListener } from '@vueuse/core';
import {
  useStrapiPost,
  useStrapiPut,
  getJwtFromLocalStorage,
} from 'src/shared/composables/useStrapiApi';
import { usePremiumAccessGuard } from 'src/modules/Course/composables/usePremiumAccessGuard';
import { UserStep } from '../models/UserStep';
import { isFatalSaveError, useProgressSaveQueue } from './useProgressSaveQueue';
import { buildStepSaveData } from '../utilities/stepSaveData';
import { notifySaveFailed } from '../views/StepView/utilities/saveFailedNotice';

const userStepRepo = useRepo(UserStep);

export interface UseProgressPersistenceOptions {
  repoUserStep: Readonly<Ref<UserStep | null>>;
  score: Readonly<Ref<number>>;
  isCompletedStep: Readonly<Ref<boolean>>;
  timeAccumulator: Readonly<Ref<number>>;
}

type ProgressSave = { userStepDocId: string; data: Partial<UserStep> };
/** Per-request axios timeout: a hung PUT must not block later saves. */
const SAVE_TIMEOUT_MS = 15_000;

/**
 * Sends the user step's progress to Strapi (one PUT in flight, coalesced,
 * retried, flushed on pagehide) and resets it (moved verbatim from useStep).
 */
export function useProgressPersistence({
  repoUserStep,
  score,
  isCompletedStep,
  timeAccumulator,
}: UseProgressPersistenceOptions) {
  const { handlePremiumAccessDenied, isPremiumAccessDenied } =
    usePremiumAccessGuard();

  let latestSave: ProgressSave | null = null;

  // One progress PUT in flight at a time; newer saves coalesce to the latest.
  const saveQueue = useProgressSaveQueue<ProgressSave>({
    send: async ({ userStepDocId, data }) => {
      const { execute, data: response } = useStrapiPut(
        `user-steps/${userStepDocId}/progress`,
        { data },
        // Failures are retried; only the final outcome is shown (onDrop).
        { suppressError: true },
      );
      await execute({ timeout: SAVE_TIMEOUT_MS });
      // Only a response for the latest state may replace the local row.
      const responseData = (response.value as { data?: unknown } | undefined)
        ?.data;
      if (responseData && !saveQueue.hasPending()) {
        userStepRepo.save(responseData as UserStep);
      }
    },
    onError: (error) => {
      if (handlePremiumAccessDenied(error)) return 'fatal';
      console.log(
        'updateStepProgress: Error updating step progress to strapi',
        error,
      );
      return isFatalSaveError(error) ? 'fatal' : 'retry';
    },
    onDrop: (error) => {
      // The premium guard already told the student.
      if (!isPremiumAccessDenied(error)) notifySaveFailed();
    },
    timeoutMs: SAVE_TIMEOUT_MS,
  });

  /**
   * Resolves once the progress saves made so far have settled (capped, so a
   * hung request cannot block navigation).
   */
  async function awaitSaves() {
    sendScheduledSave();
    await saveQueue.drain();
  }

  /** Enqueue a save scheduled for the next tick now. */
  function sendScheduledSave() {
    const save = latestSave;
    latestSave = null;
    if (save) saveQueue.enqueue(save);
  }

  // Closing / reloading the page: best effort, send what is waiting now
  // (a waiting retry included) instead of losing it with the page.
  useEventListener(window, 'pagehide', () => {
    sendScheduledSave();
    saveQueue.flush();
  });

  function updateStrapiProgress(
    userStepData: Partial<UserStep>,
    { stampStart = true }: { stampStart?: boolean } = {},
  ) {
    if (!repoUserStep.value) {
      console.log('updateStepProgress: Error updating step progress to strapi');
      return;
    }
    const stepData = buildStepSaveData(
      {
        ...userStepData,
        score: score.value,
        // Stamped once: an existing completion_date is kept.
        completion_date:
          repoUserStep.value.completion_date ??
          (isCompletedStep.value ? new Date().toISOString() : null),
        time_accumulator: timeAccumulator.value,
      },
      repoUserStep.value.start_date,
      stampStart,
    );

    // Keep local state in sync immediately so list pages show completion without reload.
    // The progress itself is already local (and may be newer than this snapshot).
    const userStepDocId = repoUserStep.value?.documentId;
    if (!userStepDocId) return;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { progress, ...derived } = stepData as Partial<UserStep>;
    userStepRepo.where('documentId', userStepDocId).update(derived);

    // Signed out: local only, nothing to send.
    if (!getJwtFromLocalStorage()) return;

    // Several saves in one tick send one PUT carrying the latest snapshot.
    const isScheduled = !!latestSave;
    latestSave = { userStepDocId, data: stepData };
    if (isScheduled) return;
    void nextTick().then(sendScheduledSave);
  }

  async function resetStepData(userStepDocId?: string) {
    // No row: nothing to reset.
    if (!repoUserStep.value?.id) return;

    // The server clears every field, so the reset is sent even when the
    // local progress is empty.
    try {
      const docId = userStepDocId ?? repoUserStep.value.documentId;
      // No stale progress PUT may settle after the reset.
      latestSave = null;
      await saveQueue.drain();
      saveQueue.cancel();
      const { execute, data } = useStrapiPost(`user-steps/${docId}/reset`);
      await execute();
      if (data.value) {
        userStepRepo.save(data.value as UserStep);
      }
    } catch (error) {
      console.log('resetStepData: Error resetting step', error);
    }
  }

  return { updateStrapiProgress, awaitSaves, resetStepData };
}

export type UseProgressPersistenceReturn = ReturnType<
  typeof useProgressPersistence
>;
